// Internal modules
import { appStore } from '@/store';
import { features } from '@/lib/features';
import { getLocale } from '@/lib/globals';
import {
  BUYER_FX_INELIGIBLE_COUNTRIES,
  FORCED_PAYPAL_CONVERSION_CARD_BRANDS,
  FORCED_VENDOR_CONVERSION_CARD_BRANDS,
} from './constants';
import { hasTreatment } from '@/lib/features/utils';

/**
 * @function isBuyerFxEligible
 * @returns {boolean}
 * @description Checks for Buyer FX eligibility based on:
 * 1. Country eligibility
 * 2. Card type eligibility
 * 3. Domestic transactions restricted to domestic currency (ex: IN)
 */
const isBuyerFxEligible = (): boolean => {
  const { api: apiState, form: formState } = appStore.getState();
  const type = formState.cardNumber.cardType || '';
  const { country: merchantCountry } = apiState.cartDetails.merchant || {};
  const { country: buyerCountry } = getLocale();
  const isDomesticTransaction = buyerCountry === merchantCountry;
  const isRestrictedDomesticTransaction =
    features.isActive('hasDccDomesticRestriction') && isDomesticTransaction;
  const isBFXEnabledOnIneligibleCountry = hasTreatment(
    'FEATURE_BFX_RAMP_UP_WEASLEY',
  );

  return (
    (!BUYER_FX_INELIGIBLE_COUNTRIES.includes(buyerCountry) ||
      isBFXEnabledOnIneligibleCountry) &&
    !FORCED_PAYPAL_CONVERSION_CARD_BRANDS.includes(type) &&
    !FORCED_VENDOR_CONVERSION_CARD_BRANDS.includes(type) &&
    !isRestrictedDomesticTransaction
  );
};

/**
 * @returns {boolean} whether or not the currency conversion is needed.
 */
const isConversionNeeded = (): boolean => {
  const { cartAmount, griffinMetadata } = appStore.getState().api;
  const { currencyCode: cartCurrency } = cartAmount ?? {};
  const { currencyCode: localeCurrency } = griffinMetadata ?? {};

  /**
   * If this function is called before the initial data is
   * returned we'll just return `false`. This shouldn't ever
   * happen since the places this function is used, serializing the form
   * and fetching conversion data after a card number is entered, aren't
   * called until we have this information.
   */
  if (!cartCurrency || !localeCurrency) {
    return false;
  }

  return cartCurrency !== localeCurrency;
};

export { isConversionNeeded, isBuyerFxEligible };
